import { cva } from 'class-variance-authority';

/**
 * Select class recipes.
 *
 * Zero `dark:` variants, as everywhere else in this layer. The closed control reads the
 * `--ds-input-*` slots and the menu the `--ds-select-*` ones, both set per theme in
 * `components/styles/design-system.css`.
 *
 * ── The closed control IS the text input's box ──────────────────────────────────────────────────
 *
 * Not a lookalike built to the same numbers — literally the same recipes. `Select` renders
 * `textInputBoxRecipe`, `textInputLabelRecipe`, `textInputHelperRecipe` and
 * `textInputAffixRecipe`, and this file adds only the parts a select has and an input does not:
 * the trigger, the caret and the menu.
 *
 * That is what makes the ticket's "must not drift from the text input" invariant true by
 * construction rather than by matching values that could later diverge. It works because Base UI's
 * `SelectRoot` feeds the surrounding `Field.Root` — `setFilled`, `setFocused`, `setTouched`,
 * `setDirty` — so `Field.Root` carries exactly the `data-focused` / `data-filled` / `data-invalid` /
 * `data-disabled` attributes the `group/ti` selectors in `TextInput.recipe` already key off. The
 * label shrinks, the fill and border step on hover, and the measured accent focus border appears
 * with no new CSS at all. The reserved halo tokens are deliberately not drawn; `TextInput.recipe`
 * records why they need explicit keyboard-modality tracking first.
 *
 * Verified against the design rather than assumed: the select's label line, value line, radius,
 * padding and 60px height are the same numbers as the text input's cells (compare nodes 498:41219
 * and 447:35138 — they differ only in the caret and, in light mode, one border step). See the note
 * on `selectCaretRecipe` for the one place the two shipped components disagree.
 */

/**
 * The trigger — a `<button>` that fills the whole box and pays all the padding itself.
 *
 * Same structural decision as the text input's control, for the same reason: pad the box instead and
 * the button becomes a thin strip across the middle of a 60px field, so most of what looks like the
 * control is dead to the pointer. Here it matters more than it does for an input, because the entire
 * field is meant to open the menu.
 *
 * `pt-[29px]` over a 20px line with `pb-[9px]` is 58px, plus the 1px border top and bottom — the
 * design's 60px, and the same arithmetic the text input uses, so the value text of a select and of
 * an input stacked beneath it sit on the same line.
 *
 * `text-left` is load-bearing: a `<button>` centres its text by default, and the value has to start
 * at the same x as the label above it.
 *
 * `pr-[44px]` is unconditional because the caret is not optional — 24px gutter, 16px of glyph, 4px
 * of gap. The value truncates against it rather than running underneath.
 *
 * The two gaps here were 10px until the library reworked both reviewed field sets to 4px beside the
 * trailing affordance and 6px beside the leading one (Select 498:38719, Text Input 447:35126). They
 * are stated per side rather than shared because they are genuinely different numbers, and they have
 * to match `textInputControlRecipe`'s or a Select and an input stacked in one `TextInputGroup` stop
 * starting their values at the same x — which is the whole reason this recipe exists instead of a
 * second copy of the input's.
 */
export const selectTriggerRecipe = cva(
  [
    'relative m-0 block w-full appearance-none border-0 bg-transparent text-left',
    'font-sans text-body-md text-[var(--ds-input-value)]',
    'outline-none',
    'pr-[44px]',
    'cursor-pointer',
    'disabled:cursor-not-allowed disabled:text-[var(--ds-input-value-disabled)]',
  ],
  {
    variants: {
      /**
       * Declared with no classes of their own: the vertical inset depends on BOTH of these at once,
       * so it is emitted from `compoundVariants` below and nowhere else.
       *
       * Split that way deliberately rather than for tidiness. A `py-[19px]` here alongside a
       * `pb-[10px]` there would put two competing utilities on one element, and which won would be
       * decided by their order in the generated stylesheet rather than by the order they are listed
       * in — `cva` composes with `clsx`, which concatenates and does not resolve Tailwind conflicts.
       */
      labelHidden: { true: '', false: '' },
      hasHelper: { true: '', false: '' },
      /** 24px gutter, plus 16px of icon and the row's 6px gap when the leading slot is filled. */
      hasLeftIcon: { true: 'pl-[46px]', false: 'pl-[24px]' },
    },
    compoundVariants: [
      /**
       * The drawn field. `pt-[29px]` clears the SHRUNK label — 9px to its line plus the line itself —
       * which is what leaves the value on the design's line at y=30, and `pb-[9px]` completes the
       * 58px interior of a 60px box. Both moved a pixel when the rework shrank the floated label to
       * 12px; `textInputControlRecipe` carries the arithmetic.
       */
      { labelHidden: false, hasHelper: false, class: 'pt-[29px] pb-[9px]' },
      // With helper text the bottom inset moves to the box — see `textInputBoxRecipe`.
      { labelHidden: false, hasHelper: true, class: 'pt-[29px] pb-0' },
      /**
       * Hidden label: nothing sits above the value, so the inset that cleared it would strand the
       * text in the bottom third of a box that now looks empty above it. `py-[19px]` centres the
       * same 20px line in the same 60px field (19 + 20 + 19, plus the box's 1px border top and
       * bottom), so only the value moves and the height is untouched.
       */
      { labelHidden: true, hasHelper: false, class: 'py-[19px]' },
      { labelHidden: true, hasHelper: true, class: 'pt-[19px] pb-0' },
    ],
    defaultVariants: { labelHidden: false, hasHelper: false, hasLeftIcon: false },
  }
);

/**
 * The value text inside the trigger.
 *
 * `truncate` needs a block with a width to work against, which the trigger's own padding gives it.
 * An empty select renders no value at all — the unshrunk label occupies the value line instead,
 * which is exactly what `Content=Empty` draws (node 498:41837). So there is no placeholder colour
 * here: there is no placeholder.
 *
 * `min-h-[20px]` is what keeps the value line present when there is no value, and it is load-bearing
 * rather than defensive. `Select.Value` renders NOTHING while empty, so without a floor this span
 * collapses to zero and the trigger measures 38px instead of 58 — the box still looks right, because
 * it has its own `min-h-[60px]`, but the bottom third of an empty field stops being a click target
 * and stops opening the menu. Caught by measuring the anchor, not by looking at it.
 */
export const selectValueRecipe = cva(['block min-h-[20px] truncate']);

/**
 * The caret: its rotation and its COLOUR — position comes from
 * `textInputAffixRecipe({ side: 'right' })`, which the component composes with this.
 *
 * Sharing the position was once a knowing two-pixel divergence from the file, raised for design
 * because both sets then drew the trailing glyph 20px from the top of the box while the shipped input
 * placed it at 22px. THE REWORK ANSWERED IT IN THE SHIPPED COMPONENT'S FAVOUR: the reviewed sets now
 * draw both trailing affordances centred on y=30, the centre of the 40px content row — Select's
 * 16px caret at y=22 (498:38719) and the input's 24px Button cell at y=18 (447:35126).
 *
 * So the divergence is closed, and the shared centre is now the file's centre rather than a
 * compromise. The Select caret keeps `textInputAffixRecipe`'s 16px right-side cell at `top-[21px]`;
 * the input's 24px Button has its own positioning recipe at `top-[17px]`.
 *
 * ── The colour is the one thing the shared affix gets wrong for a select ──────────────────────
 *
 * `textInputAffixRecipe` paints at `--ds-input-label`, which is `foreground/secondary`. Every
 * non-disabled cell in the reworked `Select` set binds the caret vector to
 * `foreground/primary/default` — one step brighter than the label beside it, because the caret is
 * the only thing on a closed select that says the field opens at all.
 *
 * So this overrides the two colour utilities and nothing else. `cn()` is `tailwind-merge`, so the
 * later `text-[…]` wins its group and the later `group-data-[disabled]/ti:text-[…]` wins its own;
 * position, sizing and the `[&_svg]:size-[16px]!` guard all stay with the shared cell. The slot is
 * shared with the leading glyph via `selectLeadingGlyphRecipe` because the file gives the two one
 * value in every cell.
 *
 * The label beside it steps on hover and on open; the caret does NOT. It is already at full
 * strength, so there is nowhere brighter to go and the file draws no step.
 *
 * The 180° flip on open is authored, not decoration: the Active cells rotate the glyph (the export
 * shows `-rotate-180 -scale-x-100` on node I505:11791). `Select.Icon` carries `data-popup-open`, so
 * the menu's open state drives it with no React state involved.
 */
export const selectCaretRecipe = cva([
  'text-[var(--ds-select-glyph)]',
  'group-data-[disabled]/ti:text-[var(--ds-select-glyph-disabled)]',
  'transition-transform duration-150 motion-reduce:transition-none',
  'data-[popup-open]:rotate-180',
]);

/**
 * The closed control's LEADING glyph — colour only, for exactly the reason the caret's is here.
 *
 * Both vectors bind `foreground/primary/default` in every non-disabled cell of the reworked set and
 * `foreground/disabled/default` when the field is disabled, so the two share one slot pair rather
 * than inviting a `-caret-*` and an `-icon-*` to drift apart.
 *
 * Composed onto `textInputAffixRecipe({ side: 'left' })` rather than replacing it, which is what
 * keeps the glyph's REVEAL — the shared left cell fades and scales the icon in with the label it
 * sits beside, keyed off `data-filled` / `data-focused`. A private cell would have had to reproduce
 * that animation to stand still, and would then be free to drift from it.
 */
export const selectLeadingGlyphRecipe = cva([
  'text-[var(--ds-select-glyph)]',
  'group-data-[disabled]/ti:text-[var(--ds-select-glyph-disabled)]',
]);

/**
 * The positioner.
 *
 * Only a stacking context — all the geometry is inline styles from Base UI's anchor positioning.
 *
 * `z-[1400]` sits above MUI's modal (1300) and drawer (1200) and below its tooltip (1500), because a
 * select inside a legacy MUI `Dialog` is the ordinary case during the refresh and a popup trapped
 * behind the dialog it belongs to is unusable. The value is MUI's scale rather than this layer's
 * because MUI is what it has to coexist with.
 *
 * The literal is REPEATED in `menuPositionerRecipe` (1400) and `dialogRecipe` (1300) rather than
 * shared from one constant, and it cannot be otherwise: Tailwind generates a utility only for a class
 * name it finds written out in the source, so `z-[${SOME_CONSTANT}]` would compile to a class with no
 * rule behind it. All three sites are named here so a change to one can find the others.
 */
export const selectPositionerRecipe = cva(['z-[1400]']);

/**
 * The menu.
 *
 * `ds-select` is repeated here by the component rather than inherited: the popup is portalled into
 * `<body>`, so it has none of the trigger's ancestors and would otherwise resolve every slot to its
 * light-mode fallback. The theme class is NOT repeated here — `Select` puts it on `Select.Portal`,
 * which is an ancestor of this element, so the ordinary `:where(.dark) .ds-select` rule reaches it.
 *
 * The design draws the panel 2px wider than the trigger and offset 1px left (node 556:20985 —
 * `left: -1px`, `right: -1px`), so the two borders' outer edges line up rather than the panel sitting
 * inside the field. `--anchor-width` is Base UI's measurement of the trigger.
 *
 * `max-h-[--available-height]` plus scrolling is what keeps a long list on screen near the bottom of
 * the viewport; Base UI computes the value from the collision boundary.
 *
 * ── The geometry, re-read on 2026-09-15 ────────────────────────────────────────────────────────
 *
 * Every number here moved with the `List-Item` rework, because the popup IS the reworked `Menu`
 * master (node 556:20624) and the select's own instance of it is node 556:20985. Read off the
 * master's bound variables rather than measured off a render:
 *
 *   radius    `radius/md`  16px, was 20
 *   padding   `x2` / `x2,5`  8px across, 10px down, was 12 / 16
 *   row gap   `x0,5`  2px, was 0
 *
 * The gap is the one that changes how the list reads rather than only its size: the rows are pills
 * now, so without air between them two adjacent hover fills would touch and read as one block.
 */
export const selectMenuRecipe = cva([
  'box-border flex flex-col gap-[2px]',
  'w-[calc(var(--anchor-width)_+_2px)]',
  'max-h-[var(--available-height)] overflow-y-auto overscroll-contain',
  'px-[8px] py-[10px]',
  'rounded-[16px]',
  'border-solid border-[length:1px] border-[var(--ds-select-menu-border)]',
  'bg-[var(--ds-select-menu-bg)]',
  'outline-none',
]);

/**
 * The list inside the panel.
 *
 * Exists ONLY to carry the 2px row gap, and it has to be here rather than on the panel: `Select.List`
 * is a single flex child of the popup, so a `gap` on the popup separates the list from the footer and
 * nothing inside it. The rows are pills now (see `selectItemRecipe`), and two adjacent hover fills
 * with no air between them read as one block rather than two rows.
 *
 * `flex flex-col` is what makes the gap apply at all — `Select.List` renders a plain `<div>`.
 */
export const selectListRecipe = cva(['flex flex-col gap-[2px]']);

/**
 * An option row — the reworked Figma `List-Item` (node 82:7424, page "List-item [Reviewed]✅").
 *
 * 16 cells across `Type` (Single Select / Multi Select) x `State` (Default / Hover / Focus /
 * Disabled) x `isSelected` (True / False). Read off the component set's bound variables on
 * 2026-09-15, not measured off a render.
 *
 * ── The geometry ───────────────────────────────────────────────────────────────────────────────
 *
 *   type      `Label/lg`  14px Medium on a 20px line, truncating to ONE line
 *   inset     `x3` / `x2`  12px across, 8px down, so a 36px row — it was 6px down and 32px
 *   gap       `x2`  8px
 *   radius    `radius/full`  a pill
 *
 * The radius was `rounded-[24px]`, derived from a concentric rule against the panel. It renders
 * identically at this height — anything over half of 36 clamps to a pill — but `rounded-full` is
 * what the cell binds, and it stays right if the row ever grows. The concentric derivation still
 * governs the MULTILINE row in `menuItemRecipe`, where a pill would be wrong.
 *
 * ── Selection is no longer a colour ────────────────────────────────────────────────────────────
 *
 * This is the substantive change in the rework, and it reverses a documented decision.
 *
 * What shipped: the selected row shared the hovered row's FILL and kept a stronger label colour to
 * stay distinguishable from it, because design feedback on HNS-362 had removed the trailing tick.
 * The consequence was recorded at the time — the selected row was a colour step and nothing else, so
 * a colour-blind user had only the announced `aria-selected`.
 *
 * What the rework draws: `isSelected=True` and `=False` are IDENTICAL in every one of the eight
 * states — same fill, same label colour. The tick is back (`selectItemCheckRecipe`), and it is the
 * whole of the selected state. So this recipe has no `data-[selected]` selector at all, and
 * `--ds-select-item-fg-selected` is gone from the stylesheet.
 *
 * Two things follow. The fill is now hover feedback ONLY, which removes the precedence question the
 * old shared fill created. And the labels could move up a step to `--foreground-primary`, since they
 * no longer have to leave room beneath a stronger selected colour.
 *
 * `data-highlighted` still covers BOTH mouse hover and keyboard navigation, because Base UI's
 * `highlightItemOnHover` defaults to true — and the cells agree, drawing one hover treatment for
 * both. `State=Focus` is a separate cell, and it is a RING rather than a fill: see below.
 *
 * ── One authoring slip in the cells, not reproduced ────────────────────────────────────────────
 *
 * `Type=Multi Select, State=Hover, isSelected=False` labels at `foreground/secondary/default`. The
 * other seven hover cells — including the single-select one beside it — carry
 * `foreground/primary/*`. Treated as a slip rather than a rule, because no axis explains why hover
 * would dim a multi-select row and not a single-select one. Raised for design.
 *
 * Selector precedence stays mutually exclusive rather than order-dependent, as in the sibling
 * recipes: disabled → highlighted → resting.
 */
export const selectItemRecipe = cva([
  'group/si',
  'relative flex w-full shrink-0 items-center gap-[8px]',
  'm-0 appearance-none border-0 bg-transparent text-left',
  'px-[12px] py-[8px]',
  'rounded-full',
  'font-sans text-label-lg select-none',
  'outline-none',
  'cursor-pointer',
  'transition-[color,background-color] duration-150 motion-reduce:transition-none',

  // Resting, out-specified by everything below. The cells bind `background/secondary/default` here,
  // which is the PANEL's own fill — so the row is transparent and inherits it, rather than painting
  // the same colour a second time. That also keeps a row correct if a caller themes the panel.
  'text-[var(--ds-select-item-fg)]',

  // Hover and keyboard highlight, which the cells draw as one treatment.
  'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-select-item-bg-hover)]',
  'data-[highlighted]:not-data-[disabled]:text-[var(--ds-select-item-fg-hover)]',

  /**
   * `State=Focus`, drawn as the design system's SHARED two-shadow ring — the one `Button`,
   * `Checkbox` and `Radio` ship, and the one the reworked cells carry as the `Focused` effect style.
   * `menuItemRecipe` documents the whole thing at length; the short version is the two substitutions.
   *
   * The GAP reads `--ds-select-item-focus-gap`, which the class points at its own panel fill, rather
   * than `effect/focus-inner`. That role carries the two `background/primary` values, because a gap
   * is meant to look like the surface the control sits on — and this popup is `background/secondary`,
   * so naming the role painted a near-white band against a grey panel. `--ds-radio-focus-backdrop`
   * is the same substitution for the same reason.
   *
   * The RING is `--ds-select-item-focus` and not the style's `effect/focus-outer` — see that slot
   * for the 2.58:1 measurement that rules the style's own value out in light mode.
   *
   * `focus-visible` rather than `focus`, so the ring answers a keyboard tab and not a pointer click.
   * The Focus cell does not say which it means, and a ring on every row the pointer touches would
   * fight the hover fill it is drawn over.
   */
  'focus-visible:shadow-[0_0_0_var(--effect-focus-inner-spread)_var(--ds-select-item-focus-gap),0_0_0_var(--effect-focus-outer-spread)_var(--ds-select-item-focus)]',

  /**
   * Disabled, which RECOLOURS rather than dims.
   *
   * It was `opacity-[var(--ds-select-disabled-opacity)]` over the row's own colours. The cells
   * author `foreground/disabled/default` explicitly, and an explicit disabled colour is what
   * `contrast.md` asks for in any case — a multiplier is a value no contrast harness can read
   * without compositing it first.
   *
   * The disabled cells keep the RESTING fill, so there is no background selector here: a disabled
   * row is the same surface as an enabled one, told apart by its label. `data-[disabled]` also
   * beats `data-[highlighted]` by exclusion above, so a disabled row cannot pick up a hover fill.
   */
  'data-[disabled]:cursor-not-allowed data-[disabled]:text-[var(--ds-select-item-fg-disabled)]',
]);

/**
 * The selected row's tick — a 16px `Check` pinned after everything else in the row.
 *
 * BACK, after design feedback on HNS-362 removed it. The reworked cells draw it on every
 * `Type=Single Select, isSelected=True` cell in `foreground/accent/default`, and it is now the whole
 * of the selected state rather than a second cue beside a colour step (see `selectItemRecipe`).
 *
 * Its own colour rather than the row's `currentColor`, which is the one place a row's glyph does not
 * inherit: the cells bind the accent for the tick while the label beside it stays
 * `foreground/primary`, and the tick has to keep the accent on a hovered row too. `--ds-select-item-
 * check` carries WCAG 1.4.11 on both fills it can land on; the numbers are on the slot.
 *
 * NOT rendered for a multi-select row. The `Type=Multi Select` cells carry a leading checkbox and no
 * trailing tick — one selection mark per row, at the end of the row for a single choice and at the
 * start for a set of them.
 */
export const selectItemCheckRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  'text-[var(--ds-select-item-check)]',
  '[&_svg]:size-full',
]);

/**
 * A per-option leading glyph. Sized and boxed like the field's own affixes so a 16px icon lands the
 * same way in both places.
 *
 * It takes its OWN colour rather than the row's `currentColor`, which is the one thing here the
 * 2026-09-15 rework changed. The cells' hidden `Waveform` layer carries `foreground/secondary/
 * default` against a `foreground/primary/default` label, and holds it on the hover cells while the
 * label brightens — so the two are not one value, and inheriting drew the glyph a step too loud.
 *
 * It DOES follow the label when the row is disabled, because that is what the disabled cells draw:
 * there the dimness is the state, not a hierarchy choice. `group/si` on the row is what reaches it.
 *
 * `text-[length:16px]` sizes a MUI-backed glyph, which draws itself at `1em` and would otherwise
 * resolve that against the row's own label size. `[&_svg]:size-full` sizes a raw one, which has no
 * font-size relationship to inherit. Two rules because neither reaches both — the same split
 * `menuItemIconRecipe` documents at length.
 */
export const selectItemIconRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  'text-[length:16px] text-[var(--ds-select-item-icon)]',
  'group-data-[disabled]/si:text-[var(--ds-select-item-fg-disabled)]',
  '[&_svg]:size-full',
]);

/** The option label. Flexes so a trailing cell stays pinned right as the row grows. */
export const selectItemLabelRecipe = cva(['min-w-0 flex-1 truncate']);

/**
 * A per-option trailing cell — the caller's own content, positioned and nothing else.
 *
 * `shrink-0` against the label's `flex-1 truncate` is the whole point: a long label truncates and
 * the cell keeps its width, rather than the two fighting over the row and the badge collapsing to
 * an ellipsis.
 *
 * No colour, no padding, no background. This layer has no status vocabulary of its own and the
 * design draws no cell here (see `types.ts`), so anything drawn would be invented chrome. A caller
 * wanting a grey pill passes `Tag`.
 */
export const selectItemTrailingRecipe = cva(['inline-flex shrink-0 items-center']);
